/**
 * 중복 패스워드 감지 및 분석 유틸리티
 * 동일한 패스워드와 유사한 패스워드를 감지합니다.
 */

import { DUPLICATE_DETECTION, SECURITY_GRADES, PASSWORD_CONFIG } from '../constants/security';

/**
 * 문자열 간의 레벤슈타인 거리를 계산
 * @param {string} str1 - 첫 번째 문자열
 * @param {string} str2 - 두 번째 문자열
 * @returns {number} 레벤슈타인 거리
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];
  
  // 빈 문자열 처리
  if (str1.length === 0) return str2.length;
  if (str2.length === 0) return str1.length;
  
  // 매트릭스 초기화
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  // 매트릭스 계산
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // 치환
          matrix[i][j - 1] + 1,     // 삽입
          matrix[i - 1][j] + 1      // 삭제
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * 두 패스워드의 유사도를 계산 (0-1, 1이 완전히 동일)
 * @param {string} password1 - 첫 번째 패스워드
 * @param {string} password2 - 두 번째 패스워드
 * @returns {number} 유사도 (0-1)
 */
function calculateSimilarity(password1, password2) {
  if (password1 === password2) return 1;
  
  const maxLength = Math.max(password1.length, password2.length);
  if (maxLength === 0) return 1;
  
  const distance = levenshteinDistance(password1, password2);
  return 1 - (distance / maxLength);
}

/**
 * 패스워드의 패턴을 분석하여 구조적 유사성을 검사
 * @param {string} password - 분석할 패스워드
 * @returns {object} 패스워드 패턴 정보
 */
function analyzePasswordPattern(password) {
  return {
    length: password.length,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumbers: /[0-9]/.test(password),
    hasSpecialChars: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    startsWithCapital: /^[A-Z]/.test(password),
    endsWithNumber: /[0-9]$/.test(password),
    hasCommonPatterns: {
      hasYear: /19[0-9]{2}|20[0-9]{2}/.test(password),
      hasSequence: /(123|abc|qwe|asdf|zxc)/i.test(password),
      hasRepetition: /(.)\1{2,}/.test(password),
      hasKeyboardPattern: /(qwerty|asdfgh|zxcvbn)/i.test(password)
    }
  };
}

/**
 * 중복 패스워드를 감지하고 분석
 * @param {Array} passwords - 패스워드 배열 
 * @param {number} similarityThreshold - 유사도 임계값 (기본값: 0.8)
 * @returns {object} 중복 분석 결과
 */
export function detectDuplicatePasswords(passwords, similarityThreshold = DUPLICATE_DETECTION.SIMILAR_PASSWORD_THRESHOLD) {
  const results = {
    exactDuplicates: [],      // 완전히 동일한 패스워드들
    similarPasswords: [],     // 유사한 패스워드들
    weakPatterns: [],         // 약한 패턴을 가진 패스워드들
    statistics: {
      totalPasswords: passwords.length,
      uniquePasswords: 0,
      duplicateGroups: 0,
      similarGroups: 0,
      riskScore: 0
    }
  };

  if (!passwords || passwords.length === 0) {
    return results;
  }

  // 패스워드 데이터 정규화
  const passwordData = passwords.map(pwd => ({
    id: pwd.id,
    title: pwd.title,
    username: pwd.username,
    website: pwd.website,
    password: pwd.password,
    pattern: analyzePasswordPattern(pwd.password)
  }));

  const processed = new Set();
  
  // 정확한 중복 찾기
  for (let i = 0; i < passwordData.length; i++) {
    if (processed.has(i)) continue;
    
    const current = passwordData[i];
    const exactMatches = [current];
    
    for (let j = i + 1; j < passwordData.length; j++) {
      if (processed.has(j)) continue;
      
      const compare = passwordData[j];
      
      if (current.password === compare.password) {
        exactMatches.push(compare);
        processed.add(j);
      }
    }
    
    if (exactMatches.length > 1) {
      results.exactDuplicates.push({
        password: current.password,
        count: exactMatches.length,
        items: exactMatches,
        riskLevel: 'high'
      });
      results.statistics.duplicateGroups++;
    }
    
    processed.add(i);
  }

  // 유사한 패스워드 찾기
  processed.clear();
  
  for (let i = 0; i < passwordData.length; i++) {
    if (processed.has(i)) continue;
    
    const current = passwordData[i];
    const similarMatches = [current];
    let maxSimilarity = 0;
    
    for (let j = i + 1; j < passwordData.length; j++) {
      if (processed.has(j)) continue;
      
      const compare = passwordData[j];
      const similarity = calculateSimilarity(current.password, compare.password);
      
      if (similarity >= similarityThreshold && similarity < 1) {
        similarMatches.push({
          ...compare,
          similarity: similarity
        });
        maxSimilarity = Math.max(maxSimilarity, similarity);
        processed.add(j);
      }
    }
    
    if (similarMatches.length > 1) {
      results.similarPasswords.push({
        basePassword: current.password,
        count: similarMatches.length,
        items: similarMatches,
        riskLevel: maxSimilarity > 0.9 ? 'high' : 'medium'
      });
      results.statistics.similarGroups++;
    }
    
    processed.add(i);
  }

  // 약한 패턴 분석
  passwordData.forEach(pwd => {
    const issues = [];
    const pattern = pwd.pattern;
    
    if (pattern.length < PASSWORD_CONFIG.MIN_LENGTH) {
      issues.push(`너무 짧은 길이 (${PASSWORD_CONFIG.MIN_LENGTH}자 미만)`);
    }
    
    if (pattern.hasCommonPatterns.hasSequence) {
      issues.push('연속된 문자나 숫자 포함 (123, abc 등)');
    }
    
    if (pattern.hasCommonPatterns.hasRepetition) {
      issues.push('동일한 문자 반복 (aaa, 111 등)');
    }
    
    if (pattern.hasCommonPatterns.hasKeyboardPattern) {
      issues.push('키보드 패턴 포함 (qwerty, asdf 등)');
    }
    
    if (!pattern.hasNumbers && !pattern.hasSpecialChars) {
      issues.push('숫자나 특수문자 미포함');
    }
    
    if (issues.length > 0) {
      results.weakPatterns.push({
        ...pwd,
        issues: issues,
        riskLevel: issues.length >= 3 ? 'high' : 'medium'
      });
    }
  });

  // 통계 계산
  const allPasswords = new Set(passwordData.map(p => p.password));
  results.statistics.uniquePasswords = allPasswords.size;
  
  // 위험 점수 계산 (0-100)
  const duplicateRisk = (results.exactDuplicates.length / passwordData.length) * 40;
  const similarRisk = (results.similarPasswords.length / passwordData.length) * 30;
  const weakRisk = (results.weakPatterns.length / passwordData.length) * 30;
  
  results.statistics.riskScore = Math.min(100, Math.round(duplicateRisk + similarRisk + weakRisk));

  return results;
}

/**
 * 새 패스워드가 기존 패스워드와 중복되는지 확인
 * @param {string} newPassword - 새 패스워드
 * @param {Array} existingPasswords - 기존 패스워드 배열
 * @param {string} excludeId - 제외할 패스워드 ID (편집시)
 * @returns {object} 중복 확인 결과
 */
export function checkPasswordDuplicate(newPassword, existingPasswords, excludeId = null) {
  const result = {
    isDuplicate: false,
    isSimilar: false,
    duplicateItems: [],
    similarItems: [],
    recommendation: ''
  };

  if (!newPassword || !existingPasswords || existingPasswords.length === 0) {
    return result;
  }

  const filteredPasswords = existingPasswords.filter(pwd => pwd.id !== excludeId);

  // 정확한 중복 확인
  const exactMatches = filteredPasswords.filter(pwd => pwd.password === newPassword);
  if (exactMatches.length > 0) {
    result.isDuplicate = true;
    result.duplicateItems = exactMatches;
    result.recommendation = `이 패스워드는 이미 ${exactMatches.length}개의 다른 계정에서 사용 중입니다. 보안을 위해 고유한 패스워드를 사용해주세요.`;
    return result;
  }

  // 유사한 패스워드 확인
  const similarMatches = filteredPasswords
    .map(pwd => ({
      ...pwd,
      similarity: calculateSimilarity(newPassword, pwd.password)
    }))
    .filter(pwd => pwd.similarity >= 0.8)
    .sort((a, b) => b.similarity - a.similarity);

  if (similarMatches.length > 0) {
    result.isSimilar = true;
    result.similarItems = similarMatches;
    const topMatch = similarMatches[0];
    result.recommendation = `이 패스워드는 "${topMatch.title}"의 패스워드와 ${Math.round(topMatch.similarity * 100)}% 유사합니다. 더 독특한 패스워드를 고려해보세요.`;
  }

  return result;
}

/**
 * 패스워드 보안 등급을 계산
 * @param {object} passwordData - 패스워드 데이터
 * @param {Array} allPasswords - 모든 패스워드 목록
 * @returns {object} 보안 등급 정보
 */
export function calculatePasswordSecurityGrade(passwordData, allPasswords) {
  let score = 100;
  const issues = [];
  
  const pattern = analyzePasswordPattern(passwordData.password);
  
  // 길이 검사
  if (pattern.length < PASSWORD_CONFIG.MIN_LENGTH) {
    score -= 30;
    issues.push('길이가 너무 짧습니다');
  } else if (pattern.length < PASSWORD_CONFIG.RECOMMENDED_MIN_LENGTH) {
    score -= 10;
    issues.push('더 긴 패스워드를 권장합니다');
  }
  
  // 복잡성 검사
  const complexity = [
    pattern.hasUpperCase,
    pattern.hasLowerCase, 
    pattern.hasNumbers,
    pattern.hasSpecialChars
  ].filter(Boolean).length;
  
  if (complexity < 3) {
    score -= 20;
    issues.push('대문자, 소문자, 숫자, 특수문자를 포함해주세요');
  }
  
  // 패턴 검사
  Object.entries(pattern.hasCommonPatterns).forEach(([key, hasPattern]) => {
    if (hasPattern) {
      score -= 15;
      issues.push('일반적인 패턴을 피해주세요');
    }
  });
  
  // 중복 검사
  const duplicateCheck = checkPasswordDuplicate(
    passwordData.password, 
    allPasswords, 
    passwordData.id
  );
  
  if (duplicateCheck.isDuplicate) {
    score -= 40;
    issues.push('중복된 패스워드입니다');
  } else if (duplicateCheck.isSimilar) {
    score -= 20;
    issues.push('유사한 패스워드가 존재합니다');
  }
  
  // 등급 결정
  let grade, color;
  if (score >= 90) {
    grade = 'A+';
    color = '#28a745';
  } else if (score >= 80) {
    grade = 'A';
    color = '#20c997';
  } else if (score >= 70) {
    grade = 'B';
    color = '#ffc107';
  } else if (score >= 60) {
    grade = 'C';
    color = '#fd7e14';
  } else {
    grade = 'D';
    color = '#dc3545';
  }
  
  return {
    score: Math.max(0, score),
    grade,
    color,
    issues,
    isSecure: score >= 80
  };
}